// The stable, surface-level verification Result that every consumer of the
// browser verifier renders — the demo pages, sequesign.com/verify, and the
// client-domain demo. It is a normalization of the core verifier's
// VerificationReport (from @sequesign/lib/verify) into a small, presentation-
// friendly shape with an ordered list of named checks. The verification LOGIC
// is unchanged and unduplicated: this only reshapes what verifyReceiptPackage
// already returned, so "same code an auditor runs" stays literally true.
//
// The normalization is deliberately faithful to the core report's own axes, so
// the demo never overstates what was proven:
//   - genesis_mandate reflects the HARD binding axis (is the receipt
//     cryptographically bound to its declared mandate — profile_hash_verified),
//     kept distinct from conformance, the SOFT axis (did the sealed work obey
//     the mandate's rules — conformant). An intentionally nonconformant receipt
//     is still bound to an intact mandate; collapsing the two would erase the
//     very distinction the "break it" demo exists to show.
//   - witness_signature is described according to trust_anchor_mode: an
//     "external" anchor is a genuine independent third-party check, while a
//     "self" anchor is an integrity self-check against the receipt's own
//     bundled witness key — never presented as independent.
//   - countersignature preserves the approval leg-state and signer summaries so
//     the L5 human-countersignature property survives normalization (a vouched
//     approval verifies the signer's identity; an unvouched one is signature-
//     valid but self-asserted).

export type CheckId =
  | "hash_integrity"
  | "sequence"
  | "agent_binding"
  | "schema_valid"
  | "genesis_mandate"
  | "conformance"
  | "policy_binding"
  | "witness_signature"
  | "completeness"
  | "inclusion_proofs"
  | "countersignature"
  | "authorship"
  // A catch-all that appears ONLY on an invalid receipt whose rejection reason
  // this normalizer does not map to one of the specific axes above (e.g. a
  // pre-verification gate rejection). It guarantees an invalid receipt can never
  // render as an all-neutral (all-green) check list.
  | "receipt_structure";

// "partial" is a distinct non-passing state: some — but not all — of an axis's
// evidence verified (currently only witness-log inclusion proofs, where some
// attestations carry a verified proof and the rest carry none). It must never be
// collapsed into "pass", or a status-only consumer would treat a
// partially-anchored receipt as fully anchored.
export type CheckStatus = "pass" | "fail" | "partial" | "not_evaluated";

// Mirrors the core report's LegState / trust_anchor_mode literals so the
// normalizer is honest about the exact axis it is reporting.
export type LegState = "absent" | "present_unverified" | "present_verified";
export type TrustAnchorMode = "self" | "external" | "none";

export interface ResultCheck {
  id: CheckId;
  status: CheckStatus;
  detail: string;
}

export interface ResultViolation {
  actionIdx: number | null;
  rule: string;
  detail: string;
}

// A signer behind the approval / counterparty leg, preserved so consumers
// bound to this contract can render and count the attestations (the defining
// property of the L5 countersigned fixture).
export interface ResultAttestation {
  id: string;
  signerId: string;
  keyFingerprint: string;
  partyType: "human" | "agent" | null;
  purpose: string;
  vouched: boolean;
}

export interface Result {
  valid: boolean;
  // The core verifier's machine failure reason on an invalid receipt, preserved
  // so a hard failure (e.g. an unsupported schema version) is never lost behind
  // default per-check flags. null when the receipt is valid.
  reason: string | null;
  checks: ResultCheck[];
  level: string;
  conformant: boolean | null;
  violations: ResultViolation[];
  // The hard-binding axis, surfaced separately from conformance so consumers
  // can render "mandate intact" independently of "work obeyed it".
  mandateBound: boolean | null;
  // Trust-anchor mode of the witness check, so a consumer never has to infer
  // independence from `witnessed` alone.
  trustAnchorMode: TrustAnchorMode | null;
  // Approval / counterparty legs + the signers behind them.
  approval: LegState;
  counterparty: LegState;
  approvals: ResultAttestation[];
  counterparties: ResultAttestation[];
  timings: { totalMs: number };
  verifierVersion: string;
  commit: string;
}

// The subset of the core VerificationReport this normalizer reads. The real
// VerificationReport (@sequesign/lib/types) structurally satisfies this, so the
// normalizer stays decoupled from the full type and is trivially unit-testable.
export interface ReportLike {
  valid: boolean;
  verification_level?: string;
  // Preserved so a malformed profile_constrained receipt (whose failure path
  // drops `profile`) is still reported as a failed mandate binding, not as a
  // freeform receipt with no mandate.
  receipt_mode?: "freeform" | "schema_validated" | "profile_constrained";
  // On a hard failure the core verifier returns a machine reason code (and
  // often human-readable details); on that path the per-check boolean flags are
  // untouched defaults (all false/null), so the real signal lives HERE, not in
  // the flags. The normalizer surfaces reason/details and never fabricates a
  // per-check failure from a default flag.
  reason?: string;
  details?: string[];
  trust_anchor_mode?: TrustAnchorMode;
  witness_trust_anchor?: {
    trusted_key_ids?: string[];
    matched_key_id?: string | null;
  } | null;
  // The HARD binding axis: present (with profile_hash_verified) only for a
  // profile_constrained receipt; absent for freeform / schema-only receipts.
  profile?: { profile_id?: string; profile_hash_verified?: boolean } | null;
  conformant?: boolean | null;
  conformance?: { conformant?: boolean | null; violations?: string[] } | null;
  // The completeness sub-report. completeness_verified:false has two distinct
  // causes: positively-detected omissions (omitted_positions present) vs merely
  // inconsistent/mismatched witness-log metadata (no omitted_positions). The
  // normalizer must not assert omission in the latter case.
  completeness?: {
    omitted_positions?: number[];
    log_entry_count?: number;
    receipt_action_count?: number;
    source?: string;
  } | null;
  template_authenticity?: "attested" | "unrecognized" | "unattested";
  agent_identity_bound?: boolean;
  identity_assurance?: "registered" | "self_asserted";
  approvals?: Array<{
    approval_id?: string;
    approver_id?: string;
    approver_key_fingerprint?: string;
    party_type?: "human" | "agent";
    approved_action_type?: string;
    vouched?: boolean;
  }> | null;
  counterparties?: Array<{
    counterparty_id?: string;
    counterparty_key_fingerprint?: string;
    attested_action_id?: string;
    attestation_purpose?: string;
    vouched?: boolean;
  }> | null;
  flags?: {
    hash_integrity?: boolean;
    sequence_integrity?: boolean;
    schema_valid?: boolean | null;
    workflow_profile_valid?: boolean | null;
    witnessed?: boolean;
    agent_identity_bound?: boolean;
    policy_bound?: boolean | null;
    completeness_verified?: boolean | null;
    inclusion_proofs_verified?: string;
    approval?: LegState;
    counterparty?: LegState;
    [k: string]: unknown;
  };
}

// The core verifier's failure reason codes, grouped by the check each one
// actually condemns. On a hard failure every per-check boolean flag is reset to
// its default (false/null), so a check is only reported as failed when the
// reason maps to it — otherwise it did not run and is "not evaluated". Any
// reason not listed here still surfaces via Result.reason and marks the
// unrelated integrity checks "not evaluated" rather than fabricating a failure.
const HASH_FAIL_REASONS = new Set([
  "evidence_hash_mismatch",
  "action_record_hash_mismatch",
  "final_chain_state_mismatch",
  "missing_evidence",
  // Altering the mandate, parameters, parties, or initial chain state breaks the
  // genesis binding; the core verifier treats this as an integrity failure.
  "genesis_binding_mismatch",
  // Tampering with the committed params.json is caught by comparing its hash to
  // profile.params_hash — an integrity (hash) failure.
  "params_binding_mismatch",
  // Other params/genesis-binding integrity failures in the same family: a
  // malformed params_hash, params present on an unparameterized receipt, or a
  // schema-version/shape correlation mismatch. All are detected by the
  // genesis/hash binding checks.
  "params_hash_malformed",
  "params_present_on_unparameterized_receipt",
  "version_params_mismatch"
]);
const SEQUENCE_FAIL_REASONS = new Set(["sequence_mismatch", "previous_chain_state_mismatch"]);
const WITNESS_FAIL_REASONS = new Set([
  "witness_key_not_trusted",
  "witness_key_id_mismatch",
  "witness_attestation_binding_mismatch"
]);
const AGENT_FAIL_REASONS = new Set([
  "agent_attestation_binding_mismatch",
  // The identity attestation claims a key fingerprint the embedded agent public
  // key does not hash to (or the key is unparseable) — a hard agent-identity
  // binding failure.
  "agent_identity_attestation_fingerprint_mismatch"
]);
// Countersignature (approval/counterparty) binding failures. These hard-fail
// reasons leave the leg flags at their default "absent", so the countersignature
// check must recognize them explicitly rather than reporting "no countersignature".
const COUNTERSIG_FAIL_REASONS = new Set([
  "approval_binding_mismatch",
  "counterparty_attestation_binding_mismatch"
]);
// Structural conformance failures: a malformed conformance block, or a
// conformance block on an unconstrained receipt. These reject the receipt with
// no conformance verdict, so map them to a failed conformance check.
const CONFORMANCE_FAIL_REASONS = new Set([
  "conformance_block_malformed",
  "conformance_on_unconstrained_receipt"
]);
const POLICY_FAIL_REASONS = new Set(["policy_binding_failed"]);
const INCLUSION_FAIL_REASONS = new Set([
  "inclusion_proof_invalid",
  "inclusion_proof_malformed",
  "inclusion_proof_attestation_mismatch",
  "inclusion_proof_key_mismatch",
  "inclusion_proof_signature_invalid"
]);
// Mandate-binding-specific failure reasons. Only these should mark the
// genesis_mandate check failed when the report carries no resolved profile; a
// profile_constrained receipt that hard-failed earlier on an unrelated axis
// (e.g. evidence_hash_mismatch) never reached mandate evaluation, so its
// mandate check is "not evaluated", not failed.
const MANDATE_FAIL_REASONS = new Set([
  "missing_profile_binding",
  "unknown_profile",
  "profile_binding_invalid",
  "embedded_profile_unreadable"
]);

// True when the receipt's mode declares a mandate/schemas at all — used to
// describe a skipped check accurately (a mandate/schema that was declared but
// not reached) instead of calling the receipt freeform.
function declaresMandate(report: ReportLike): boolean {
  return report.receipt_mode === "profile_constrained";
}
function declaresSchemas(report: ReportLike): boolean {
  return (
    report.receipt_mode === "schema_validated" || report.receipt_mode === "profile_constrained"
  );
}

function failureDetail(report: ReportLike, fallback: string): string {
  const bits: string[] = [];
  if (report.reason) bits.push(report.reason);
  if (report.details && report.details.length > 0) bits.push(report.details.join("; "));
  return bits.length > 0 ? bits.join(": ") : fallback;
}

// Some reason codes carry a ": human-readable detail" suffix (e.g.
// "genesis_binding_mismatch: recomputed …", "unsupported_schema_version: …").
// Match on the leading snake_case code so a suffix never defeats the lookup.
function reasonCode(reason: string | undefined): string | undefined {
  if (!reason) return undefined;
  const m = /^([a-z0-9_]+)/.exec(reason);
  return m ? m[1] : reason;
}

function reasonIn(report: ReportLike, set: Set<string>): boolean {
  const code = reasonCode(report.reason);
  return code !== undefined && set.has(code);
}

// hash_integrity / sequence are true only on the success path; a hard failure
// resets them to false regardless of what actually went wrong. So: true → pass;
// otherwise fail ONLY when the failure reason maps to this axis, else the check
// never ran (not evaluated) and the real reason is surfaced separately.
function integrityCheck(
  id: "hash_integrity" | "sequence",
  value: boolean | null | undefined,
  failReasons: Set<string>,
  report: ReportLike,
  pass: string,
  fail: string
): ResultCheck {
  if (value === true) return { id, status: "pass", detail: pass };
  if (reasonIn(report, failReasons))
    return { id, status: "fail", detail: failureDetail(report, fail) };
  return {
    id,
    status: "not_evaluated",
    detail: report.valid
      ? "Not evaluated for this receipt."
      : "Verification stopped before this check ran — see the failure reason."
  };
}

// Parse the core verifier's violation strings (e.g. "Action
// payment_instruction_created violates the parameterized mandate: content.x: …")
// into a structured, render-friendly shape. Falls back to the raw string.
function parseViolation(raw: string): ResultViolation {
  const m = /^Action\s+(\S+)\s+(.*)$/.exec(raw);
  return m
    ? { actionIdx: null, rule: `action:${m[1]}`, detail: m[2] ?? raw }
    : { actionIdx: null, rule: "mandate", detail: raw };
}

// The HARD binding axis. Derived from profile.profile_hash_verified, NOT from
// flags.workflow_profile_valid: an intentionally nonconformant receipt sets
// workflow_profile_valid:false while profile_hash_verified stays true — it is
// still cryptographically bound to an intact mandate, it just violated the
// rules. Conformance (below) is the separate soft axis.
function genesisMandateCheck(report: ReportLike): ResultCheck {
  const bound = mandateBound(report);
  if (bound === null) {
    // A profile_constrained receipt DECLARES a mandate. If it hard-failed on a
    // mandate-specific reason (e.g. missing_profile_binding), the binding is
    // genuinely broken → fail. But if it failed earlier on an unrelated axis
    // (e.g. evidence_hash_mismatch), mandate evaluation never ran → not
    // evaluated, described as skipped rather than "no mandate".
    if (declaresMandate(report)) {
      if (reasonIn(report, MANDATE_FAIL_REASONS))
        return {
          id: "genesis_mandate",
          status: "fail",
          detail: failureDetail(
            report,
            "This receipt declares a workflow mandate but its mandate binding is missing or could not be resolved."
          )
        };
      return {
        id: "genesis_mandate",
        status: "not_evaluated",
        detail:
          "This receipt declares a workflow mandate, but verification stopped before the mandate binding was evaluated."
      };
    }
    return {
      id: "genesis_mandate",
      status: "not_evaluated",
      detail: "No workflow mandate declared (freeform or schema-only receipt)."
    };
  }
  return bound
    ? {
        id: "genesis_mandate",
        status: "pass",
        detail: "The receipt is cryptographically bound to its declared workflow mandate."
      }
    : {
        id: "genesis_mandate",
        status: "fail",
        // false covers both a hash mismatch and a rejected mandate identity
        // (profile_binding_invalid with a matching hash but a differing
        // profile_id). Echo the core reason when present rather than always
        // asserting a hash mismatch.
        detail: failureDetail(
          report,
          "The receipt is not bound to the workflow mandate it declares — the mandate binding was rejected."
        )
      };
}

function mandateBound(report: ReportLike): boolean | null {
  // A mandate-binding rejection means the receipt is NOT correctly bound to its
  // claimed mandate even when the profile HASH matched — e.g.
  // profile_binding_invalid fires with profile_hash_verified:true when the
  // authenticated profile_id differs from the one the receipt claims. The
  // verifier rejected the mandate identity, so this axis is a failure, not a
  // pass, regardless of the hash flag.
  if (!report.valid && reasonIn(report, MANDATE_FAIL_REASONS)) return false;
  const p = report.profile;
  if (p === null || p === undefined || p.profile_hash_verified === undefined) return null;
  return p.profile_hash_verified;
}

// The witness check, described honestly per trust_anchor_mode so a self-anchored
// integrity check is never presented as an independent third-party witness.
function witnessCheck(report: ReportLike): ResultCheck {
  const witnessed = report.flags?.witnessed;
  const mode = report.trust_anchor_mode;
  if (mode === "none")
    return {
      id: "witness_signature",
      status: "not_evaluated",
      detail: "This receipt carries no witness attestation."
    };
  if (!witnessed) {
    // A VALID receipt whose mode is self/external DID have a witness anchor and
    // ran signature verification; witnessed:false here is an evaluated failure
    // (a signature did not verify, or not every chain extension was
    // countersigned), not a step that was skipped.
    if (report.valid && (mode === "self" || mode === "external"))
      return {
        id: "witness_signature",
        status: "fail",
        detail:
          "A witness signature did not verify, or not every chain extension was countersigned."
      };
    // On a hard failure, witnessed:false is an untouched default — fail ONLY
    // when the failure reason is witness-specific; otherwise the step did not
    // run.
    if (reasonIn(report, WITNESS_FAIL_REASONS))
      return {
        id: "witness_signature",
        status: "fail",
        detail: failureDetail(report, "The witness countersignature did not verify.")
      };
    return {
      id: "witness_signature",
      status: "not_evaluated",
      detail:
        witnessed === undefined
          ? "This receipt carries no witness attestation."
          : "Witness verification did not run for this receipt."
    };
  }
  if (mode === "external") {
    const matched = report.witness_trust_anchor?.matched_key_id;
    return {
      id: "witness_signature",
      status: "pass",
      detail: matched
        ? `An independent witness countersigned each chain extension, verified against a trusted external key (${matched}).`
        : "An independent witness countersigned each chain extension, verified against a trusted external key."
    };
  }
  // "self", or an unspecified mode with a valid witness signature: the
  // signature verifies against the key bundled with the receipt. That is an
  // integrity self-check, NOT an independent third-party anchor.
  return {
    id: "witness_signature",
    status: "pass",
    detail:
      "A witness countersignature is present and verifies against the key bundled with the receipt — an integrity self-check, not an independent third-party anchor."
  };
}

// Evidence-schema validation (the defining L2 property). schema_valid is a
// genuine tri-state on the report: null = not requested (freeform), true =
// every evidence blob validated, false = a blob failed its schema. It is not
// reset by the pre-check fail() paths (those leave it null), so it can be read
// directly — this is what distinguishes an L1 freeform receipt (not_evaluated)
// from an L2 schema_validated receipt (pass).
function schemaCheck(report: ReportLike): ResultCheck {
  const s = report.flags?.schema_valid;
  if (s === true)
    return {
      id: "schema_valid",
      status: "pass",
      detail: "Every evidence blob validates against its declared schema."
    };
  if (s === false)
    return {
      id: "schema_valid",
      status: "fail",
      detail: failureDetail(report, "An evidence blob failed schema validation.")
    };
  // schema_valid is null: either the receipt declares no schemas (freeform), or
  // an L2/L3 receipt hard-failed before schema validation ran. Describe the
  // skipped case accurately instead of calling a schema_validated receipt
  // freeform.
  return {
    id: "schema_valid",
    status: "not_evaluated",
    detail: declaresSchemas(report)
      ? "Verification stopped before evidence-schema validation ran for this receipt."
      : "No evidence schemas declared for this receipt (freeform)."
  };
}

// Agent-signature binding: whether every action is signed by the declared agent
// key (agentSignaturesOk && one attestation per action). A VALID receipt with
// agent_identity_bound:false means the actions are NOT authenticated by the
// declared agent — a real negative the trust ladder reflects by staying below
// L2 — so it must surface, not hide behind the passing integrity checks.
function agentBindingCheck(report: ReportLike): ResultCheck {
  const bound = report.flags?.agent_identity_bound;
  // agent_identity_bound:true is set ONLY when the check genuinely passed (the
  // fail() default is false). It is trustworthy regardless of report.valid — a
  // later hard failure (e.g. policy_binding_failed) preserves the already-passed
  // true — so honor it first.
  if (bound === true) {
    const assured = report.identity_assurance === "registered";
    return {
      id: "agent_binding",
      status: "pass",
      detail: assured
        ? "Every action is signed by the declared agent key, whose identity is vouched by a trusted registration."
        : "Every action is signed by the declared agent key (a self-asserted identity — not vouched by a registration)."
    };
  }
  if (report.valid) {
    // On a valid receipt, false is a genuine negative (actions not agent-bound).
    if (bound === false)
      return {
        id: "agent_binding",
        status: "fail",
        detail:
          "The actions are not all signed by the declared agent key (missing or invalid agent signatures), so they are not bound to the agent identity."
      };
    return { id: "agent_binding", status: "not_evaluated", detail: "Agent binding not evaluated." };
  }
  // Invalid receipt with bound !== true: agent_identity_bound is an untouched
  // default — fail only on an agent-specific reason, else the step did not run.
  if (reasonIn(report, AGENT_FAIL_REASONS))
    return {
      id: "agent_binding",
      status: "fail",
      detail: failureDetail(report, "An agent-signature binding check failed.")
    };
  return {
    id: "agent_binding",
    status: "not_evaluated",
    detail: "Agent-signature verification did not run for this receipt."
  };
}

// Witness-log completeness: whether the witness's log confirms no actions were
// omitted from the sealed chain. completeness_verified is tri-state (null = not
// checked, true = complete, false = omissions positively detected). A valid
// receipt can carry false — the verifier's strongest "this package is
// incomplete" signal — so it must surface as a failure, not be dropped.
function completenessCheck(report: ReportLike): ResultCheck {
  const c = report.flags?.completeness_verified;
  if (c === true)
    return {
      id: "completeness",
      status: "pass",
      detail: "The witness log confirms no actions were omitted from this chain."
    };
  if (c === false) {
    // false has two causes: positively-detected omissions (omitted_positions
    // present) vs inconsistent/mismatched witness-log metadata (no omissions
    // identified). Only assert omission in the former case.
    const omitted = report.completeness?.omitted_positions;
    return {
      id: "completeness",
      status: "fail",
      detail:
        omitted && omitted.length > 0
          ? `The witness log shows ${omitted.length} action${omitted.length === 1 ? "" : "s"} omitted from this package (position${omitted.length === 1 ? "" : "s"} ${omitted.join(", ")}) — the record is incomplete.`
          : "The witness log does not confirm this package is complete — its log metadata is inconsistent or the action counts do not match."
    };
  }
  return {
    id: "completeness",
    status: "not_evaluated",
    detail: "Completeness against a witness log was not checked for this receipt."
  };
}

// Policy binding: when the task/actions declare a policy_context_hash, every
// action must commit to the same one. policy_bound is true (bound), null (not
// requested), or false. A false is a hard failure (policy_binding_failed) — but
// baseFlags also defaults it to false on unrelated early exits, so only treat it
// as a failure when the reason is policy-specific.
function policyCheck(report: ReportLike): ResultCheck {
  const p = report.flags?.policy_bound;
  if (p === true)
    return {
      id: "policy_binding",
      status: "pass",
      detail: "Every action commits to the same declared policy context."
    };
  if (reasonIn(report, POLICY_FAIL_REASONS))
    return {
      id: "policy_binding",
      status: "fail",
      detail: failureDetail(
        report,
        "The actions do not all commit to the same declared policy context."
      )
    };
  return {
    id: "policy_binding",
    status: "not_evaluated",
    detail: "No policy context was declared for this receipt."
  };
}

// Inclusion proofs: whether the witness-log Merkle inclusion proofs verified.
// inclusion_proofs_verified is "passed" | "partial" | "failed" | "not_present".
// A hard inclusion failure (inclusion_proof_invalid / _malformed) resets the
// flag to its "not_present" default, so gate the fail on the reason too.
function inclusionCheck(report: ReportLike): ResultCheck {
  const state = report.flags?.inclusion_proofs_verified;
  if (reasonIn(report, INCLUSION_FAIL_REASONS))
    return {
      id: "inclusion_proofs",
      status: "fail",
      detail: failureDetail(report, "A witness-log inclusion proof did not verify.")
    };
  if (state === "passed")
    return {
      id: "inclusion_proofs",
      status: "pass",
      detail: "Every witness attestation's log-inclusion proof verified."
    };
  if (state === "failed")
    return {
      id: "inclusion_proofs",
      status: "fail",
      detail: failureDetail(report, "A witness-log inclusion proof did not verify.")
    };
  if (state === "partial")
    return {
      id: "inclusion_proofs",
      // NOT "pass": only some attestations are anchored. A status-only consumer
      // must not treat this as fully anchored.
      status: "partial",
      detail:
        "Only some witness attestations carried a verified inclusion proof; the rest had none attached — the package is not fully anchored."
    };
  return {
    id: "inclusion_proofs",
    status: "not_evaluated",
    detail: "No witness-log inclusion proofs were present for this receipt."
  };
}

// The countersignature check, preserving the L5 defining property. It evaluates
// BOTH the approval and counterparty legs (a counterparty-only workflow must not
// read as "no countersignature"), and describes the signers by their actual
// party_type — an agent approval is never labelled a human countersignature.
function countersignatureCheck(report: ReportLike): ResultCheck {
  const approvalLeg = report.flags?.approval ?? "absent";
  const counterpartyLeg = report.flags?.counterparty ?? "absent";
  const approvals = report.approvals ?? [];
  const counterparties = report.counterparties ?? [];
  if (approvalLeg === "absent" && counterpartyLeg === "absent") {
    // A hard failure on a countersignature-binding reason leaves the leg flags
    // at their default "absent" — but the check DID run and rejected a
    // replayed/incorrectly-bound attestation. Report that as a failure, not "no
    // countersignature".
    if (reasonIn(report, COUNTERSIG_FAIL_REASONS))
      return {
        id: "countersignature",
        status: "fail",
        detail: failureDetail(report, "A countersignature failed its binding check.")
      };
    return {
      id: "countersignature",
      status: "not_evaluated",
      detail: "No approval or counterparty countersignature in this receipt."
    };
  }

  const segments: string[] = [];
  if (approvalLeg !== "absent") {
    const humans = approvals.filter((a) => a.party_type === "human").length;
    const agents = approvals.filter((a) => a.party_type === "agent").length;
    if (approvals.length === 0) {
      segments.push("an approval signature");
    } else {
      const who = [humans > 0 ? `${humans} human` : null, agents > 0 ? `${agents} agent` : null]
        .filter(Boolean)
        .join(" + ");
      segments.push(
        `${approvals.length} approval signature${approvals.length === 1 ? "" : "s"}` +
          (who ? ` (${who})` : "")
      );
    }
  }
  if (counterpartyLeg !== "absent") {
    segments.push(
      counterparties.length > 0
        ? `${counterparties.length} counterparty confirmation${counterparties.length === 1 ? "" : "s"}`
        : "a counterparty confirmation"
    );
  }

  const anyVouched = approvalLeg === "present_verified" || counterpartyLeg === "present_verified";
  const vouchNote = anyVouched
    ? "at least one signer's identity is vouched by a trusted registration"
    : "the signers' identities are self-asserted, not vouched by a trusted registration";
  return {
    id: "countersignature",
    status: "pass",
    detail: `Countersignatures verify: ${segments.join(" and ")}; ${vouchNote}.`
  };
}

function authorshipCheck(report: ReportLike): ResultCheck {
  const auth = report.template_authenticity;
  if (auth === undefined)
    return { id: "authorship", status: "not_evaluated", detail: "Authorship not evaluated." };
  if (auth === "attested")
    return {
      id: "authorship",
      status: "pass",
      detail: "The template author's signature verified against a trusted key."
    };
  // "unattested" covers several materially different outcomes: no author
  // signature was embedded, OR one was embedded but is malformed / failed to
  // verify / is from an unrecognized author. The core verifier records the
  // specifics in details, so surface them rather than asserting "no signature".
  return {
    id: "authorship",
    status: "not_evaluated",
    detail:
      auth === "unattested"
        ? "No trusted template-author attestation was verified (no signature was embedded, or an embedded one did not verify)."
        : // "unrecognized": a signature is present but the signed author_id has no
          // trusted key configured, so it was never checked — a missing trust
          // anchor, NOT a cryptographic verification failure.
          "The template author's signature was not checked — no trusted key is configured for the signing author."
  };
}

export function toResult(
  report: ReportLike,
  opts: { totalMs: number; verifierVersion: string; commit: string }
): Result {
  const f = report.flags ?? {};
  const conformant = report.conformant ?? report.conformance?.conformant ?? null;
  const violations = (report.conformance?.violations ?? []).map(parseViolation);

  const conformanceCheck: ResultCheck =
    conformant === null
      ? reasonIn(report, CONFORMANCE_FAIL_REASONS)
        ? {
            // A structurally invalid conformance block (malformed, or present on
            // an unconstrained receipt) — the conformance axis itself is broken.
            id: "conformance",
            status: "fail",
            detail: failureDetail(
              report,
              "The receipt's conformance block is structurally invalid."
            )
          }
        : {
            id: "conformance",
            status: "not_evaluated",
            // null has two causes: a freeform/schema-only receipt has no mandate
            // to conform to, versus a profile_constrained receipt that hard-failed
            // before conformance was reached — describe the latter as skipped, not
            // as having no mandate.
            detail: declaresMandate(report)
              ? "This receipt declares a workflow mandate, but verification stopped before conformance was evaluated."
              : "No mandate to conform to (freeform or schema-only receipt)."
          }
      : {
          id: "conformance",
          status: conformant ? "pass" : "fail",
          detail: conformant
            ? "The recorded work conforms to the mandate's rules."
            : `The work does NOT conform to the mandate: ${violations.map((v) => v.detail).join("; ")}`
        };

  const checks: ResultCheck[] = [
    integrityCheck(
      "hash_integrity",
      f.hash_integrity,
      HASH_FAIL_REASONS,
      report,
      "Every action record and evidence blob hashes to its committed value.",
      "A record or evidence blob has been modified since sealing."
    ),
    integrityCheck(
      "sequence",
      f.sequence_integrity,
      SEQUENCE_FAIL_REASONS,
      report,
      "The action chain is in its original order and reproduces every state.",
      "The action order has been altered."
    ),
    agentBindingCheck(report),
    schemaCheck(report),
    genesisMandateCheck(report),
    conformanceCheck,
    policyCheck(report),
    witnessCheck(report),
    completenessCheck(report),
    inclusionCheck(report),
    countersignatureCheck(report),
    authorshipCheck(report)
  ];

  // Catch-all: an invalid receipt must never render as an all-neutral list. If
  // no specific axis surfaced the failure (a rejection reason this normalizer
  // does not map — including pre-verification gate rejections), append an honest
  // receipt_structure failure carrying the reason. Reasons this normalizer DOES
  // map already produce a failed axis above, so this only fires for the residue.
  if (!report.valid && !checks.some((c) => c.status === "fail")) {
    checks.push({
      id: "receipt_structure",
      status: "fail",
      detail: failureDetail(report, "The receipt was rejected by the verifier.")
    });
  }

  const approvals: ResultAttestation[] = (report.approvals ?? []).map((a) => ({
    id: a.approval_id ?? "",
    signerId: a.approver_id ?? "",
    keyFingerprint: a.approver_key_fingerprint ?? "",
    partyType: a.party_type ?? null,
    purpose: a.approved_action_type ?? "",
    vouched: a.vouched ?? false
  }));
  const counterparties: ResultAttestation[] = (report.counterparties ?? []).map((c) => ({
    id: c.attested_action_id ?? "",
    signerId: c.counterparty_id ?? "",
    keyFingerprint: c.counterparty_key_fingerprint ?? "",
    partyType: null,
    purpose: c.attestation_purpose ?? "",
    vouched: c.vouched ?? false
  }));

  return {
    valid: report.valid,
    reason: report.valid ? null : (report.reason ?? null),
    checks,
    level: report.verification_level ?? "NONE",
    conformant,
    violations,
    mandateBound: mandateBound(report),
    trustAnchorMode: report.trust_anchor_mode ?? null,
    approval: f.approval ?? "absent",
    counterparty: f.counterparty ?? "absent",
    approvals,
    counterparties,
    timings: { totalMs: opts.totalMs },
    verifierVersion: opts.verifierVersion,
    commit: opts.commit
  };
}
